import { useEffect, useState } from 'react';
import { Pencil, Trash2, X } from 'lucide-react';
import { useAuthedApi } from '../../lib/useAuthedApi';
import { SlugField, resolvedSlug, type SlugValue } from '../../components/SlugField';
import QrCodeButton from '../../components/QrCodeButton';
import { bookingUrl } from '../../lib/bookingUrl';

type Location = {
  id: string;
  name: string;
  slug: string;
  address: string;
  timezone: string;
  is_virtual: number;
  review_url: string | null;
  is_active: number;
};

const DEFAULT_HOURS = {
  sun: [],
  mon: [{ open: '09:00', close: '17:00' }],
  tue: [{ open: '09:00', close: '17:00' }],
  wed: [{ open: '09:00', close: '17:00' }],
  thu: [{ open: '09:00', close: '17:00' }],
  fri: [{ open: '09:00', close: '17:00' }],
  sat: [],
};

type Editing = {
  id: string;
  name: string;
  address: string;
  is_virtual: boolean;
  review_url: string;
} | null;

export default function Locations() {
  const apiFn = useAuthedApi();
  const [items, setItems] = useState<Location[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', address: '', is_virtual: false, review_url: '' });
  const [slug, setSlug] = useState<SlugValue>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  // Needed to build each location's public booking URL for its QR code.
  const [orgSlug, setOrgSlug] = useState<string | null>(null);

  const load = () =>
    apiFn<Location[]>('/locations')
      .then(setItems)
      .catch((e) => setErr(e.message));

  useEffect(() => {
    void load();
    apiFn<{ slug: string }>('/organizations/me')
      .then((o) => setOrgSlug(o.slug))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function create() {
    if (!form.name || (!form.is_virtual && !form.address)) {
      setErr(form.is_virtual ? 'Name is required.' : 'Name and address are required.');
      return;
    }
    setCreating(true);
    setErr(null);
    try {
      // Omit review_url when blank — the API validates it as a URL, so '' would
      // be rejected; undefined just leaves it null (falls back to org-wide).
      const reviewUrl = form.review_url.trim() || undefined;
      const body = form.is_virtual
        ? {
            name: form.name,
            slug: resolvedSlug(slug, form.name),
            is_virtual: true,
            review_url: reviewUrl,
          }
        : {
            name: form.name,
            address: form.address,
            slug: resolvedSlug(slug, form.name),
            hours: DEFAULT_HOURS,
            review_url: reviewUrl,
          };
      await apiFn<Location>('/locations', { body });
      setForm({ name: '', address: '', is_virtual: false, review_url: '' });
      setSlug(null);
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setCreating(false);
    }
  }

  function startEdit(l: Location) {
    setErr(null);
    setEditing({
      id: l.id,
      name: l.name,
      address: l.address,
      is_virtual: !!l.is_virtual,
      review_url: l.review_url ?? '',
    });
  }

  function cancelEdit() {
    setEditing(null);
  }

  async function saveEdit() {
    if (!editing) return;
    setSavingEdit(true);
    setErr(null);
    try {
      await apiFn<Location>(`/locations/${editing.id}`, {
        method: 'PATCH',
        // Virtual locations have no address — only the name is editable. The
        // review link sends '' to clear (falls back to the org-wide link).
        body: editing.is_virtual
          ? { name: editing.name, review_url: editing.review_url.trim() }
          : {
              name: editing.name,
              address: editing.address,
              review_url: editing.review_url.trim(),
            },
      });
      setEditing(null);
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSavingEdit(false);
    }
  }

  async function deactivate(id: string, name: string) {
    if (!confirm(`Deactivate ${name}? It'll stop appearing in the booking flow.`)) return;
    try {
      await apiFn(`/locations/${id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <div>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Add a location</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          We&rsquo;ll look up the address and set the timezone. Defaults to
          Mon&ndash;Fri, 9&ndash;5; adjust per-staff availability later.
        </p>
        <label className="row" style={{ gap: '0.5rem', alignItems: 'center', cursor: 'pointer', marginBottom: '0.75rem' }}>
          <input
            type="checkbox"
            checked={form.is_virtual}
            onChange={(e) => setForm({ ...form, is_virtual: e.target.checked })}
          />
          <span>Virtual / online location (no physical address)</span>
        </label>

        {form.is_virtual ? (
          <label>
            <span>Name</span>
            <input
              placeholder="Online / Anywhere"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <p className="field-help">
              No address or map pin. Bookees pick it as &ldquo;Online&rdquo;; the
              staff member&rsquo;s calendar link is the only &ldquo;address.&rdquo;
            </p>
          </label>
        ) : (
          <div className="grid-2">
            <label>
              <span>Name</span>
              <input
                placeholder="Downtown"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label>
              <span>Address</span>
              <input
                placeholder="123 Main St, City"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </label>
          </div>
        )}

        <label>
          <span>URL slug</span>
          <SlugField
            derivedFromName={form.name}
            value={slug}
            onChange={setSlug}
            prefix="/locations/"
            placeholder="downtown"
          />
          <p className="field-help">
            Auto-generated from the name. Click the pencil to customize.
          </p>
        </label>

        <label>
          <span>Review link <span className="muted small">(optional)</span></span>
          <input
            placeholder="https://g.page/r/your-google-review-link"
            value={form.review_url}
            onChange={(e) => setForm({ ...form, review_url: e.target.value })}
          />
          <p className="field-help">
            Used in follow-up emails for this location. Blank uses your org-wide link.
          </p>
        </label>
        {err && !editing && <p className="err">{err}</p>}
        <button className="primary" onClick={create} disabled={creating}>
          {creating ? 'Adding…' : 'Add location'}
        </button>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>
          Your locations {items.length > 0 && <span className="muted small">({items.length})</span>}
        </h3>

        {items.length === 0 ? (
          <p className="muted">No locations yet. Add one above to get started.</p>
        ) : (
          <table className="bare">
            <tbody>
              {items.map((l) => {
                const isEditingThis = editing?.id === l.id;
                if (isEditingThis && editing) {
                  return (
                    <tr key={l.id}>
                      <td colSpan={4} style={{ padding: '1rem' }}>
                        <div className="row" style={{ marginBottom: '0.5rem' }}>
                          <strong>Edit location</strong>
                          <span className="spacer" />
                          <button className="ghost" onClick={cancelEdit} aria-label="Cancel edit">
                            <X size={16} />
                          </button>
                        </div>
                        {editing.is_virtual ? (
                          <label>
                            <span>Name</span>
                            <input
                              value={editing.name}
                              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                            />
                            <p className="field-help">Virtual / online location — no address.</p>
                          </label>
                        ) : (
                          <div className="grid-2">
                            <label>
                              <span>Name</span>
                              <input
                                value={editing.name}
                                onChange={(e) =>
                                  setEditing({ ...editing, name: e.target.value })
                                }
                              />
                            </label>
                            <label>
                              <span>Address</span>
                              <input
                                value={editing.address}
                                onChange={(e) =>
                                  setEditing({ ...editing, address: e.target.value })
                                }
                              />
                            </label>
                          </div>
                        )}

                        <label>
                          <span>Review link <span className="muted small">(optional)</span></span>
                          <input
                            placeholder="https://g.page/r/your-google-review-link"
                            value={editing.review_url}
                            onChange={(e) =>
                              setEditing({ ...editing, review_url: e.target.value })
                            }
                          />
                          <p className="field-help">
                            Used in follow-up emails for this location. Blank uses
                            your org-wide link.
                          </p>
                        </label>

                        {err && <p className="err">{err}</p>}
                        <div className="row" style={{ gap: '0.5rem' }}>
                          <button
                            className="primary"
                            onClick={saveEdit}
                            disabled={
                              savingEdit ||
                              !editing.name.trim() ||
                              (!editing.is_virtual && !editing.address.trim())
                            }
                          >
                            {savingEdit ? 'Saving…' : 'Save changes'}
                          </button>
                          <button className="ghost" onClick={cancelEdit}>
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={l.id}>
                    <td>
                      <div>
                        {l.name}
                        {l.is_virtual === 1 && (
                          <span className="badge badge-muted" style={{ marginLeft: '0.5rem' }}>
                            Online
                          </span>
                        )}
                      </div>
                      <div className="muted small">
                        {l.is_virtual === 1 ? 'Virtual — no address' : l.address}
                      </div>
                      {l.review_url && (
                        <div className="muted small">Review link set</div>
                      )}
                    </td>
                    <td className="muted small">{l.timezone}</td>
                    <td>
                      {l.is_active === 1 ? (
                        <span className="badge badge-ok">Active</span>
                      ) : (
                        <span className="badge badge-muted">Inactive</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {orgSlug && l.is_active === 1 && (
                        <QrCodeButton
                          className="icon-btn"
                          iconOnly
                          url={bookingUrl(orgSlug, `/locations/${l.slug}`)}
                          title={l.name}
                          filename={`${orgSlug}-${l.slug}`}
                        />
                      )}
                      <button
                        className="icon-btn"
                        onClick={() => startEdit(l)}
                        title="Edit"
                        aria-label={`Edit ${l.name}`}
                      >
                        <Pencil size={16} />
                      </button>
                      {l.is_active === 1 && (
                        <button
                          className="icon-btn icon-btn-danger"
                          onClick={() => deactivate(l.id, l.name)}
                          title="Deactivate"
                          aria-label={`Deactivate ${l.name}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
